/**
 * Pure color helpers: hex parsing, contrast, title sizing, slug helpers, and
 * small dependency-free color space conversions.
 */

export type Rgb = [number, number, number]

/** True for "#abc", "#aabbcc", "abc", "aabbcc" (case-insensitive). */
export function isHex(value: string): boolean {
  return /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.test(value.trim())
}

/** Normalize any accepted hex form to "#rrggbb" lowercase. */
export function normalizeHex(value: string): string {
  let h = value.trim().toLowerCase().replace(/^#/, '')
  if (h.length === 3) h = h.split('').map((c) => c + c).join('')
  return '#' + h
}

export function hexToRgb(hex: string): Rgb {
  const h = normalizeHex(hex).slice(1)
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)]
}

export function rgbToHex([r, g, b]: Rgb): string {
  const to = (n: number) => Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, '0')
  return '#' + to(r) + to(g) + to(b)
}

/**
 * Perceived brightness test: returns the text color that
 * reads best on top of the given background.
 */
export function hexContrast(hex: string): 'black' | 'white' {
  const [r, g, b] = hexToRgb(hex)
  const brightness = (Math.round(r * 299) + Math.round(g * 587) + Math.round(b * 114)) / 1000
  return brightness >= 128 ? 'black' : 'white'
}

export function randomHex(): string {
  const n = Math.floor(Math.random() * 0x1000000)
  return '#' + n.toString(16).padStart(6, '0')
}

/**
 * Extra rem added to the giant color-page title so short names render larger.
 * (18 - min(len, 18)) / 8.
 */
export function titleSize(name: string): number {
  const max = 18
  const len = Math.min(name.length, max)
  return (max - len) / 8
}

/** "My Favorite Blue" -> "my-favorite-blue" */
export function createColorPath(name: string): string {
  return name.trim().replace(/\s+/g, '-').toLowerCase()
}

/** "my-favorite-blue" -> "My Favorite Blue" */
export function slugToName(slug: string): string {
  return slug
    .split('-')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
}

export function hexToHsl(hex: string): [number, number, number] {
  const [r, g, b] = hexToRgb(hex).map((v) => v / 255)
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const l = (max + min) / 2
  let h = 0
  let s = 0
  if (max !== min) {
    const d = max - min
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
    if (max === r) h = (g - b) / d + (g < b ? 6 : 0)
    else if (max === g) h = (b - r) / d + 2
    else h = (r - g) / d + 4
    h *= 60
  }
  return [Math.round(h), Math.round(s * 100), Math.round(l * 100)]
}

export function hexToHsv(hex: string): [number, number, number] {
  const [r, g, b] = hexToRgb(hex).map((v) => v / 255)
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const d = max - min
  let h = 0
  if (d !== 0) {
    if (max === r) h = ((g - b) / d) % 6
    else if (max === g) h = (b - r) / d + 2
    else h = (r - g) / d + 4
    h *= 60
    if (h < 0) h += 360
  }
  const s = max === 0 ? 0 : d / max
  return [Math.round(h), Math.round(s * 100), Math.round(max * 100)]
}

export function hexToCmyk(hex: string): [number, number, number, number] {
  const [r, g, b] = hexToRgb(hex).map((v) => v / 255)
  const k = 1 - Math.max(r, g, b)
  if (k === 1) return [0, 0, 0, 100]
  const c = (1 - r - k) / (1 - k)
  const m = (1 - g - k) / (1 - k)
  const y = (1 - b - k) / (1 - k)
  return [Math.round(c * 100), Math.round(m * 100), Math.round(y * 100), Math.round(k * 100)]
}

/** CIE XYZ (D65), scaled 0..100, rounded to integers. */
export function hexToXyz(hex: string): [number, number, number] {
  const lin = hexToRgb(hex).map((v) => {
    const c = v / 255
    return c > 0.04045 ? Math.pow((c + 0.055) / 1.055, 2.4) : c / 12.92
  })
  const [r, g, b] = lin
  const x = r * 0.4124 + g * 0.3576 + b * 0.1805
  const y = r * 0.2126 + g * 0.7152 + b * 0.0722
  const z = r * 0.0193 + g * 0.1192 + b * 0.9505
  return [Math.round(x * 100), Math.round(y * 100), Math.round(z * 100)]
}

export function hexToLab(hex: string): [number, number, number] {
  const lin = hexToRgb(hex).map((v) => {
    const c = v / 255
    return c > 0.04045 ? Math.pow((c + 0.055) / 1.055, 2.4) : c / 12.92
  })
  const [r, g, b] = lin
  let x = (r * 0.4124 + g * 0.3576 + b * 0.1805) / 0.95047
  let y = r * 0.2126 + g * 0.7152 + b * 0.0722
  let z = (r * 0.0193 + g * 0.1192 + b * 0.9505) / 1.08883
  const f = (t: number) => (t > 0.008856 ? Math.cbrt(t) : 7.787 * t + 16 / 116)
  x = f(x)
  y = f(y)
  z = f(z)
  return [Math.round(116 * y - 16), Math.round(500 * (x - y)), Math.round(200 * (y - z))]
}

export function hexToLch(hex: string): [number, number, number] {
  const [l, a, b] = hexToLab(hex)
  const c = Math.sqrt(a * a + b * b)
  let h = (Math.atan2(b, a) * 180) / Math.PI
  if (h < 0) h += 360
  return [l, Math.round(c), Math.round(h)]
}

/** Hue, chroma, gray: color-convert's HCG model. */
export function hexToHcg(hex: string): [number, number, number] {
  const [r, g, b] = hexToRgb(hex).map((v) => v / 255)
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const chroma = max - min
  const gray = chroma < 1 ? min / (1 - chroma) : 0
  let hue = 0
  if (chroma > 0) {
    if (max === r) hue = ((g - b) / chroma) % 6
    else if (max === g) hue = 2 + (b - r) / chroma
    else hue = 4 + (r - g) / chroma
    hue = ((hue / 6) % 1) * 360
    if (hue < 0) hue += 360
  }
  return [Math.round(hue), Math.round(chroma * 100), Math.round(gray * 100)]
}

export interface ColorValues {
  hex: string
  rgb: Rgb
  hsl: [number, number, number]
  hsv: [number, number, number]
  cmyk: [number, number, number, number]
  lab: [number, number, number]
  xyz: [number, number, number]
  lch: [number, number, number]
  hcg: [number, number, number]
  contrast: 'black' | 'white'
}

export function describeColor(hex: string): ColorValues {
  const h = normalizeHex(hex)
  return {
    hex: h,
    rgb: hexToRgb(h),
    hsl: hexToHsl(h),
    hsv: hexToHsv(h),
    cmyk: hexToCmyk(h),
    lab: hexToLab(h),
    xyz: hexToXyz(h),
    lch: hexToLch(h),
    hcg: hexToHcg(h),
    contrast: hexContrast(h),
  }
}
